import { lazy, type ComponentType } from "react";

type ClientRoute = { path: string; component: ComponentType };

const page = (loader: () => Promise<{ default: ComponentType }>) => lazy(loader);
const Home = page(() => import("@/pages/Home"));
const Services = page(() => import("@/pages/Services"));
const About = page(() => import("@/pages/About"));
const Blog = page(() => import("@/pages/Blog"));
const BlogPost = page(() => import("@/pages/BlogPost"));
const CaseStudies = page(() => import("@/pages/CaseStudies"));
const CaseStudyDetail = page(() => import("@/pages/CaseStudyDetail"));
const Contact = page(() => import("@/pages/Contact"));
const PrivacyPolicy = page(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = page(() => import("@/pages/TermsOfService"));
const Accessibility = page(() => import("@/pages/Accessibility"));
const Events = page(() => import("@/pages/Events"));
const Glossary = page(() => import("@/pages/Glossary"));
const EwasteStats = page(() => import("@/pages/EwasteStats"));
const EwasteProcessing = page(() => import("@/pages/EwasteProcessing"));
const Certifications = page(() => import("@/pages/Certifications"));
const FAQPage = page(() => import("@/pages/FAQPage"));
const ItemsWeAccept = page(() => import("@/pages/ItemsWeAccept"));
const Reviews = page(() => import("@/pages/Reviews"));
const NotFound = page(() => import("@/pages/not-found"));

const fixedRoutes: ClientRoute[] = [
  ["/", Home], ["/services", Services], ["/about", About], ["/blog", Blog],
  ["/case-studies", CaseStudies], ["/events", Events], ["/contact", Contact],
  ["/privacy-policy", PrivacyPolicy], ["/terms-of-service", TermsOfService],
  ["/accessibility", Accessibility], ["/glossary", Glossary],
  ["/ewaste-stats", EwasteStats], ["/ewaste-processing", EwasteProcessing],
  ["/certifications", Certifications], ["/faq", FAQPage],
  ["/items-we-accept", ItemsWeAccept], ["/reviews", Reviews],
].map(([path, component]) => ({ path: path as string, component: component as ComponentType }));

const serviceAreaRoutes = [
  ["houston-tx", "HoustonPage"], ["katy-tx", "KatyPage"],
  ["sugarland-tx", "SugarlandPage"], ["cinco-ranch-tx", "CincoRanchPage"],
  ["richmond-tx", "RichmondPage"], ["fulshear-tx", "FulshearPage"],
  ["cypress-tx", "CypressPage"], ["the-woodlands-tx", "TheWoodlandsPage"],
  ["memorial-tx", "MemorialPage"], ["river-oaks-tx", "RiverOaksPage"],
  ["the-heights-tx", "TheHeightsPage"], ["montrose-tx", "MontrosePage"],
  ["westchase-tx", "WestchasePage"], ["bellaire-tx", "BellairePage"],
  ["jersey-village-tx", "JerseyVillagePage"], ["spring-tx", "SpringPage"],
  ["energy-corridor-tx", "EnergyCorridorPage"], ["greenway-plaza-tx", "GreenwayPlazaPage"],
  ["texas-medical-center-tx", "TexasMedicalCenterPage"], ["galleria-tx", "GalleriaPage"],
  ["missouri-city-tx", "MissouriCityPage"], ["stafford-tx", "StaffordPage"],
  ["pecan-grove-tx", "PecanGrovePage"], ["pearland-tx", "PearlandPage"],
] as const;

const commercialRoutes = [
  ["property-management", "PropertyManagementPage"], ["oil-and-gas", "OilAndGasPage"],
  ["churches", "ChurchesPage"], ["schools", "SchoolsPage"],
  ["healthcare", "HealthcarePage"], ["law-firms", "LawFirmsPage"],
  ["warehouses-logistics", "WarehousesPage"], ["hotels-hospitality", "HotelsPage"],
  ["government", "GovernmentPage"], ["retail", "RetailPage"],
  ["real-estate-offices", "RealEstatePage"], ["it-companies", "ITCompaniesPage"],
] as const;

const serviceConceptRoutes = [
  ["/data-destruction", "DataDestructionPage"],
  ["/it-asset-disposition", "ITAssetDispositionPage"],
  ["/server-decommissioning", "ServerDecommissioningPage"],
  ["/office-cleanout", "OfficeCleanoutPage"],
  ["/e-waste-collection-events", "CollectionEventsPage"],
] as const;

export const runtimeRoutes: ClientRoute[] = [
  ...fixedRoutes,
  ...serviceAreaRoutes.map(([slug, exportName]) => ({
    path: `/service-areas/${slug}`,
    component: page(() => import("@/pages/ServiceArea").then((module) => ({
      default: module[exportName] as ComponentType,
    }))),
  })),
  ...commercialRoutes.map(([slug, exportName]) => ({
    path: `/commercial/${slug}`,
    component: page(() => import("@/pages/CommercialVertical").then((module) => ({
      default: module[exportName] as ComponentType,
    }))),
  })),
  ...serviceConceptRoutes.map(([path, exportName]) => ({
    path,
    component: page(() => import("@/pages/ServiceConcept").then((module) => ({
      default: module[exportName],
    }))),
  })),
];

export const dynamicRoutes: ClientRoute[] = [
  { path: "/case-studies/:slug", component: CaseStudyDetail },
  { path: "/blog/:slug", component: BlogPost },
];

export const redirectRoutes = [
  { from: "/home", to: "/" },
  { from: "/service-areas/houston", to: "/service-areas/houston-tx" },
  { from: "/service-areas/katy", to: "/service-areas/katy-tx" },
  { from: "/service-areas/sugarland", to: "/service-areas/sugarland-tx" },
  { from: "/service-areas/cinco-ranch", to: "/service-areas/cinco-ranch-tx" },
  { from: "/service-areas/richmond", to: "/service-areas/richmond-tx" },
  { from: "/service-areas/fulshear", to: "/service-areas/fulshear-tx" },
  { from: "/service-areas/cypress", to: "/service-areas/cypress-tx" },
  { from: "/service-areas/memorial", to: "/service-areas/memorial-tx" },
  { from: "/service-areas/river-oaks", to: "/service-areas/river-oaks-tx" },
  { from: "/service-areas/the-heights", to: "/service-areas/the-heights-tx" },
  { from: "/service-areas/montrose", to: "/service-areas/montrose-tx" },
  { from: "/service-areas/westchase", to: "/service-areas/westchase-tx" },
  { from: "/service-areas/bellaire", to: "/service-areas/bellaire-tx" },
  { from: "/service-areas/spring", to: "/service-areas/spring-tx" },
  { from: "/service-areas/energy-corridor", to: "/service-areas/energy-corridor-tx" },
  { from: "/houston-e-waste-pickup", to: "/service-areas/houston-tx" },
  { from: "/katy-e-waste-pickup", to: "/service-areas/katy-tx" },
  { from: "/sugar-land-e-waste-pickup", to: "/service-areas/sugarland-tx" },
  { from: "/the-woodlands-e-waste-pickup", to: "/service-areas/the-woodlands-tx" },
  { from: "/pearland-e-waste-pickup", to: "/service-areas/pearland-tx" },
  { from: "/spring-e-waste-pickup", to: "/service-areas/spring-tx" },
  { from: "/cypress-e-waste-pickup", to: "/service-areas/cypress-tx" },
  { from: "/what-we-accept", to: "/items-we-accept" },
  { from: "/how-it-works", to: "/#how-it-works" },
] as const;

export const notFound = NotFound;